import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
    Star,
    User,
    Calendar,
    ClipboardList,
    Check,
    BadgeCheck,
    X,
    ClipboardPen,
} from 'lucide-react';
import Avatar from '@mui/material/Avatar';
import { capitalize } from 'lodash';

import { FlexColumn, Flex } from 'src/components/flex';
import ExpandableItem from 'src/components/lead-sidebar/LeadSidebarExpandableItem';
import type { ILead, IStageHistory } from 'src/services/api';
import { ELeadStage } from 'src/services/api/leads';
import { normalizeSource } from 'src/pages/landlord/analytics/utils';
import type { IOtherUser } from 'src/api/users-api';
import { normalizeIngestionMethod } from 'src/components/lead-sidebar/normalize-ingestion-method';
import { LeadUtils } from 'src/utils/LeadUtils';

import styles from 'src/components/lead-sidebar/LeadStageTimeline.module.css';
import Typography from '@mui/material/Typography';

dayjs.extend(relativeTime);

const MAX_INGESTION_SOURCE_LENGTH = 20;

const getIngestionSourceWithEllipsis = (source: string): string => {
    if (source.length <= MAX_INGESTION_SOURCE_LENGTH) {
        return source;
    }
    return `${source.substring(0, MAX_INGESTION_SOURCE_LENGTH)}...`;
};

export default function LeadStageTimeline({
    lead,
    stages,
    teamMembers,
    isExpanded = true
}: {
    lead: ILead,
    stages: IStageHistory[],
    teamMembers: IOtherUser[],
    isExpanded?: boolean
}) {
    const leadOwner = teamMembers.find(member => member.id === lead.owner);

    const allStages = [
        {
            id: 1,
            label: 'New lead created',
            stageValue: ELeadStage.NEW,
            icon: <Star size={14} />,
            details: `From ${getIngestionSourceWithEllipsis(normalizeSource(lead.ingestion_source))} via ${normalizeIngestionMethod(lead.ingestion_method)}`
        },
        {
            id: 2,
            label: 'Lead contacted',
            stageValue: ELeadStage.CONTACTED,
            icon: <User size={14} />,
            details: `${leadOwner?.name} contacted the lead`
        },
        {
            id: 3,
            label: 'Tour requested',
            stageValue: ELeadStage.TOUR_REQUESTED,
            icon: <Calendar size={14} />,
            details:`Tour requested by ${LeadUtils.getLeadNameWithEllipsis(lead.name)}`
        },
        {
            id: 4,
            label: 'Tour scheduled',
            stageValue: ELeadStage.TOUR_SCHEDULED,
            icon: <Calendar size={14} />,
            details:`Tour scheduled for ${LeadUtils.getLeadNameWithEllipsis(lead.name)}`
        },
        {
            id: 5,
            label: 'Application sent',
            stageValue: ELeadStage.INVITED_TO_APPLY,
            icon: <ClipboardList size={14} />,
            details:`Application sent to ${LeadUtils.getLeadNameWithEllipsis(lead.name)}`
        },
        {
            id: 6,
            label: 'Application completed',
            stageValue: ELeadStage.APPLICATION_COMPLETED,
            icon: <Check size={14} />,
            details:`Application completed by ${LeadUtils.getLeadNameWithEllipsis(lead.name)}`
        },
        {
            id: 7,
            label: 'Application approved',
            stageValue: ELeadStage.APPLICATION_APPROVED,
            icon: <BadgeCheck size={14} />,
            details:`Application approved for ${LeadUtils.getLeadNameWithEllipsis(lead.name)}`
        },
        {
            id: 8,
            label: 'Not Interested',
            stageValue: ELeadStage.NOT_INTERESTED,
            icon: <X size={14} />,
            details:`${LeadUtils.getLeadNameWithEllipsis(lead.name)} no longer interested`
        },
        {
            id: 9,
            label: 'Not Qualified',
            stageValue: ELeadStage.NOT_QUALIFIED,
            icon: <X size={14} />,
            details:`${LeadUtils.getLeadNameWithEllipsis(lead.name)} is not qualified`
        },
        {
            id: 10,
            label: 'Lease Signed',
            stageValue: ELeadStage.LEASE_SIGNED,
            icon: <ClipboardPen size={14} />,
            details:`${LeadUtils.getLeadNameWithEllipsis(lead.name)} has signed the lease`
        },
    ];

    // Create timeline events from stage history, sorted by creation date
    const timelineEvents = useMemo(() => {
        return [
            {
                stage: allStages.find(s => s.stageValue === ELeadStage.NEW)!,
                timestamp: lead.inserted_at,
                isNewLeadStage: true,
                timeValue: capitalize(dayjs.utc(lead.inserted_at).fromNow())
            },
            ...stages
                .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                .map(history => ({
                    stage: allStages.find(s => s.stageValue === history.current_value)!,
                    timestamp: history.created_at,
                    isNewLeadStage: false,
                    history,
                    timeValue: capitalize(dayjs.utc(history.created_at).fromNow())
                }))
        ].filter(event => event.stage);
    }, [allStages, lead, stages, isExpanded]);

    return (
        <ExpandableItem title="Timeline" defaultExpanded={isExpanded}>
            <div style={{ position: 'relative', paddingLeft: 48 }}>
                <FlexColumn gap={1}>
                    {timelineEvents.map((event, index) => {
                        const { stage, timestamp } = event;
                        const isLastEvent = index === timelineEvents.length - 1;

                        return (
                            <Flex key={`${stage.stageValue}-${timestamp}`} alignItems="flex-start" style={{ position: 'relative' }}>
                                {/* Vertical line - only show if not the last event */}
                                {!isLastEvent && (
                                    <div className={styles.timelineLine} />
                                )}

                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: (stage.stageValue === ELeadStage.NOT_QUALIFIED || stage.stageValue === ELeadStage.NOT_INTERESTED) ? '#EF4444' : '#10B981',
                                        color: '#fff',
                                        position: 'absolute',
                                        left: -48,
                                    }}
                                >
                                    {stage.icon}
                                </Avatar>

                                <FlexColumn gap={0}>
                                    <div className={styles.eventLabel}>
                                        {stage.label}
                                    </div>

                                    <Typography className={styles.eventDetails} variant="body2" color="textSecondary">
                                        {stage.details}
                                    </Typography>

                                    <Flex alignItems="center" className={styles.eventTimestamp}>
                                        <Calendar size={16} color="#6B7280" />
                                        {event.timeValue}
                                    </Flex>
                                </FlexColumn>
                            </Flex>
                        );
                    })}
                </FlexColumn>
            </div>
        </ExpandableItem>
    );
}
